import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Car,
  Cpu,
  Users as UsersIcon,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import PageHeader from "@/components/shared/PageHeader";
import KPITile from "@/components/shared/KPITile";
import SectionCard from "@/components/shared/SectionCard";
import DataTable from "@/components/shared/DataTable";
import SeverityBadge from "@/components/shared/SeverityBadge";
import StatusBadge from "@/components/shared/StatusBadge";
import MapView from "@/components/shared/MapView";
import LocationLabel from "@/components/shared/LocationLabel";
import api, { extractApiError } from "@/lib/api";
import { useAlert } from "@/hooks/useAlert";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import { useVehicleStateSocket } from "@/hooks/useVehicleStateSocket";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// ── Live-map helpers ───────────────────────────────────────────────────────

function mapNormalizeBulkVehicle(item, { partial = false } = {}) {
  // Parse lat/lon as numbers (returned as strings from API)
  const lat = parseFloat(item.lat || item.latitude);
  const lon = parseFloat(item.lon || item.longitude);
  const speed = parseFloat(item.gps_speed);
  const obdSpeed = parseFloat(item.obd_speed);
  const vehicleId = item.vehicle_id || item.vehicleId || null;
  const registrationNumber =
    item.registration_number
    || item.registration
    || item.vehicle_number
    || item.vehicle_no
    || "";
  const modelText = [item.make, item.model].filter(Boolean).join(" ").trim();
  const hasStatus = item?.status !== undefined && item?.status !== null && String(item.status).trim() !== "";
  const statusText = hasStatus ? String(item.status).trim().toLowerCase() : (partial ? null : "offline");
  
  return {
    id: vehicleId || registrationNumber || String(Math.random()),
    vehicle_id: vehicleId,
    plate: registrationNumber,
    // Field mapping: make → brand, model → model
    model: modelText || (partial ? "" : "Vehicle"),
    vehicle_category: item.vehicle_category || null,
    // Keep device fields present, but vehicle_id is now the join key.
    device_id: item.device_id || item.device || null,
    device: item.device_id || item.device || null,
    // owner_name: from registration or provided owner field
    owner: item.owner_name || item.driver_name || registrationNumber || "",
    // status is computed from live Redis: active|idle|alert|offline
    status: statusText || "offline",
    coords: Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null,
    location: Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null,
    gps_speed: Number.isFinite(speed) ? speed : null,
    obd_speed: Number.isFinite(obdSpeed) ? obdSpeed : null,
    event: item.event || null,
  };
}

function mergeVehicleState(existing, incoming) {
  if (!existing) return incoming;
  if (!incoming) return existing;

  const nextModel = incoming.model && incoming.model !== "Vehicle"
    ? incoming.model
    : (existing.model || incoming.model || "Vehicle");

  return {
    ...existing,
    ...incoming,
    id: existing.id || incoming.id,
    vehicle_id: incoming.vehicle_id || existing.vehicle_id,
    plate: incoming.plate || existing.plate || "",
    model: nextModel,
    owner: incoming.owner || existing.owner || "",
    status: incoming.status || existing.status || "offline",
    coords: incoming.coords || existing.coords || null,
    location: incoming.location || existing.location || null,
    gps_speed: Number.isFinite(incoming.gps_speed) ? incoming.gps_speed : existing.gps_speed,
    obd_speed: Number.isFinite(incoming.obd_speed) ? incoming.obd_speed : existing.obd_speed,
  };
}

function getVehicleDisplayLabel(vehicle) {
  if (!vehicle || typeof vehicle !== "object") return "Vehicle";
  return (
    vehicle.plate
    || vehicle.registration_number
    || vehicle.registration
    || vehicle.model
    || "Unregistered vehicle"
  );
}

const ALLOWED_SEVERITIES = ["critical", "high", "medium", "low"];
const ALLOWED_STATUSES = [
  "open",
  "pending",
  "acknowledged",
  "dispatched",
  "on_route",
  "on_scene",
  "completed",
  "resolved",
  "closed",
];

function normalizeSeverity(value) {
  const normalized = String(value || "low").toLowerCase().trim();
  return ALLOWED_SEVERITIES.includes(normalized) ? normalized : "low";
}

function normalizeStatus(value) {
  const normalized = String(value || "pending").toLowerCase().trim().replace(/\s+/g, "_");
  return ALLOWED_STATUSES.includes(normalized) ? normalized : "pending";
}

function formatIncidentType(value) {
  const raw = String(value || "Unknown").trim();
  return raw
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatEventTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mapIncident(item, index) {
  // Extract coordinates: API uses lat/lon (not lng), handle both nested and flat formats
  const lat = item?.location?.lat || item?.lat || item?.latitude;
  const lon = item?.location?.lon || item?.lon || item?.longitude;
  const hasCoords = Number.isFinite(Number(lat)) && Number.isFinite(Number(lon));
  
  // Extract primary vehicle: IncidentRead includes vehicles array with role: "PRIMARY"
  const primaryVehicle = Array.isArray(item?.vehicles) 
    ? item.vehicles.find(v => v.role === "PRIMARY") || item.vehicles[0] 
    : null;

  return {
    id: item?.id || item?.incident_id || item?.uuid || `INC-${index + 1}`,
    displayId: `INC-${String(index + 1).padStart(4, "0")}`,
    type: formatIncidentType(item?.type || item?.incident_type || item?.category),
    // vehicle plate: registration_number from incident or from primary vehicle
    vehicle:
      item?.registration_number ||
      item?.vehicle ||
      item?.vehicle_number ||
      item?.vehicle_no ||
      primaryVehicle?.registration_number ||
      "—",
    // driver name: from driver object or fall back to driver_name field
    driver: item?.driver?.name || item?.driver_name || "—",
    severity: normalizeSeverity(item?.severity || item?.priority),
    status: normalizeStatus(item?.status),
    coords: hasCoords ? [Number(lat), Number(lon)] : null,
    location: item?.location && typeof item.location === "object" ? item.location : (hasCoords ? { lat: Number(lat), lon: Number(lon) } : null),
    eta: formatEventTime(
      item?.event_time || item?.created_at || item?.eta || item?.estimated_arrival || item?.estimated_time_to_arrival,
    ),
    // Include service_statuses for future use (maps service responses by org_type)
    serviceStatuses: item?.service_statuses || {},
  };
}

export default function AdminDashboard() {
  const { incrementLiveAlertBadge } = useAlert();
  const navigate = useNavigate();
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [recentIncidentsLoading, setRecentIncidentsLoading] = useState(true);
  const [recentIncidentsError, setRecentIncidentsError] = useState("");
  const [kpiCounts, setKpiCounts] = useState({
    customers: 0,
    customerVehicles: 0,
    emergencyServices: 0,
    emergencyServiceVehicles: 0,
  });
  const [kpiCountsLoading, setKpiCountsLoading] = useState(true);

  // Response Time Trend (data from /analytics/incidents/response-times)
  const [responseTimeData, setResponseTimeData] = useState([]);
  const [responseTimeLoading, setResponseTimeLoading] = useState(true);
  const [responseTimeError, setResponseTimeError] = useState("");
  const [responseTimeBucket, setResponseTimeBucket] = useState("day");

  // Accident trend (data from /analytics/incidents/timeseries)
  const [accidentData, setAccidentData] = useState([]);
  const [accidentLoading, setAccidentLoading] = useState(true);
  const [accidentError, setAccidentError] = useState("");
  const [accidentBucket, setAccidentBucket] = useState("month");

  // ── Live map state ────────────────────────────────────────────────────────
  const [mapVehicles, setMapVehicles] = useState([]);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapLastUpdated, setMapLastUpdated] = useState(null);
  const [criticalTab, setCriticalTab] = useState("incident");
  const [selectedCriticalFocus, setSelectedCriticalFocus] = useState(null);
  const mapVehicleById = useRef(new Map());
  const mapShownIncidents = useRef(new Set());

  // Build map markers from live vehicle positions
  const vehicleMarkers = mapVehicles
    .filter((v) => v.coords)
    .map((v) => ({
      markerId: v.id,
      coords: v.coords,
      title: getVehicleDisplayLabel(v),
      subtitle: `${v.model || "Vehicle"} · ${v.status || "offline"}`,
      severity: v.status === "alert" ? "critical" : v.status === "active" ? "low" : v.status === "idle" ? "medium" : "high",
      markerType: "vehicle",
    }));

  // Also render incident markers from /incidents response (location.lat/lon)
  const incidentMarkers = recentIncidents
    .filter((i) => i.coords)
    .map((i) => ({
      coords: i.coords,
      title: i.displayId || i.id,
      subtitle: `${i.type} · ${i.vehicle}`,
      severity: i.severity,
    }));

  const mapMarkers = [...incidentMarkers, ...vehicleMarkers];

  const criticalIncidentItems = recentIncidents.filter((incident) => Array.isArray(incident.coords) && incident.coords.length === 2);
  const criticalVehicleItems = mapVehicles.filter((vehicle) => Array.isArray(vehicle.coords) && vehicle.coords.length === 2);

  const selectedFocusCoords = selectedCriticalFocus?.coords || null;
  const selectedFocusLabel = selectedCriticalFocus?.label || null;

  const handleFocusCriticalIncident = useCallback((incident) => {
    setCriticalTab("incident");
    setSelectedCriticalFocus({
      coords: incident.coords,
      label: incident.type || "Incident",
      subtitle: incident.location || "Unknown location",
      kind: "incident",
    });
  }, []);

  const handleFocusCriticalVehicle = useCallback((vehicle) => {
    setCriticalTab("vehicle");
    setSelectedCriticalFocus({
      coords: vehicle.coords,
      vehicleId: vehicle.id,
      label: getVehicleDisplayLabel(vehicle),
      subtitle: vehicle.location || "Unknown location",
      kind: "vehicle",
    });
  }, []);

  const visibleMapMarkers = useMemo(() => {
    if (selectedCriticalFocus?.kind === "vehicle" && selectedCriticalFocus?.vehicleId) {
      const selectedVehicleMarker = vehicleMarkers.find((m) => String(m.markerId) === String(selectedCriticalFocus.vehicleId));
      return selectedVehicleMarker ? [selectedVehicleMarker] : mapMarkers;
    }
    return mapMarkers;
  }, [mapMarkers, selectedCriticalFocus, vehicleMarkers]);

  // ── /vehicles/bulk-state — seed map on load, refresh every 10 s ──────────
  const fetchBulkState = useCallback(async () => {
    try {
      const response = await api.get("/vehicles/bulk-state", { params: { only_live: true, page_size: 100 } });
      // Extract data array from { data: [...], meta: {...} } or direct array response
      const items = response?.data?.data || response?.data || [];
      const normalized = Array.isArray(items)
        ? items.map((item) => mapNormalizeBulkVehicle(item, { partial: false }))
        : [];
      setMapVehicles(normalized);
      const m = new Map();
      normalized.forEach((v) => {
        const key = v.vehicle_id || v.id;
        if (key) m.set(String(key), v);
      });
      mapVehicleById.current = m;
      setMapLastUpdated(new Date());
    } catch (err) {
      const { message: errMsg } = extractApiError(err);
      console.warn("[Dashboard] Bulk state fetch failed:", errMsg || "Failed to load vehicles");
    } finally {
      setMapLoading(false);
    }
  }, []);

  // Fetch response time series for the chart
  useEffect(() => {
    let mounted = true;

    const fetchResponseTimes = async () => {
      setResponseTimeLoading(true);
      setResponseTimeError("");

      try {
        const resp = await api.get("/analytics/incidents/response-times", {
          params: { bucket: responseTimeBucket },
        });

        const payload = resp?.data || resp || {};
        const series = payload?.series || payload?.data || [];

        const mapped = Array.isArray(series)
          ? series.map((p, i) => ({
              bucket: p?.bucket ?? p?.day ?? p?.label ?? String(i + 1),
              police: Number(p?.police ?? p?.police_time ?? 0),
              ambulance: Number(p?.ambulance ?? p?.ems ?? 0),
              fire: Number(p?.fire ?? 0),
              overall: Number(p?.overall ?? p?.avg ?? 0),
            }))
          : [];

        if (!mounted) return;
        setResponseTimeData(mapped);
      } catch (err) {
        const { message: errMsg } = extractApiError(err);
        if (!mounted) return;
        setResponseTimeError(errMsg || "Failed to load response times");
        setResponseTimeData([]);
        console.error("[Dashboard] Response time fetch failed:", errMsg || err);
      } finally {
        if (mounted) setResponseTimeLoading(false);
      }
    };

    fetchResponseTimes();

    return () => {
      mounted = false;
    };
  }, [responseTimeBucket]);

  // Fetch accident timeseries for the chart
  useEffect(() => {
    let mounted = true;

    const fetchAccidentSeries = async () => {
      setAccidentLoading(true);
      setAccidentError("");

      try {
        const resp = await api.get("/analytics/incidents/timeseries", {
          params: { bucket: accidentBucket },
        });

        const payload = resp?.data || resp || {};
        const series = payload?.series || payload?.data || [];

        const mapped = Array.isArray(series)
          ? series.map((p) => ({ month: p?.bucket ?? p?.label ?? "", count: Number(p?.count ?? 0) }))
          : [];

        if (!mounted) return;
        setAccidentData(mapped);
      } catch (err) {
        const { message: errMsg } = extractApiError(err);
        if (!mounted) return;
        setAccidentError(errMsg || "Failed to load accident timeseries");
        setAccidentData([]);
        console.error("[Dashboard] Accident timeseries fetch failed:", errMsg || err);
      } finally {
        if (mounted) setAccidentLoading(false);
      }
    };

    fetchAccidentSeries();

    return () => {
      mounted = false;
    };
  }, [accidentBucket]);

  useEffect(() => {
    // Seed bulk state once on mount; live updates come from WebSocket
    fetchBulkState();
    // No polling: subscribe to vehicle_state via WebSocket to update incrementally
    // see useVehicleStateSocket below
  }, [fetchBulkState]);

  // Subscribe to vehicle_state WebSocket messages and update mapVehicles incrementally
  useVehicleStateSocket((payload) => {
    try {
      const normalized = mapNormalizeBulkVehicle(payload, { partial: true });
      if (!normalized || !normalized.id) return;

      setMapVehicles((prev) => {
        const idx = prev.findIndex((v) => String(v.id) === String(normalized.id));
        if (idx !== -1) {
          const merged = mergeVehicleState(prev[idx], normalized);
          const next = [...prev];
          next[idx] = merged;
          return next;
        }
        return [normalized, ...prev];
      });

      // keep vehicle_id -> vehicle map in sync
      const m = mapVehicleById.current || new Map();
      const key = normalized.vehicle_id || normalized.id;
      if (key) m.set(String(key), normalized);
      mapVehicleById.current = m;

      setMapLastUpdated(new Date());
    } catch (e) {
      // ignore malformed WS payloads
      if (process.env.NODE_ENV !== "production") console.warn("[Dashboard] vehicle_state WS handler error", e);
    }
  });

  useEffect(() => {
    let mounted = true;

    const fetchKpiCounts = async () => {
      setKpiCountsLoading(true);
      try {
        const response = await api.get("/analytics/counts");
        const payload = response?.data?.data || response?.data || {};
        const counts = payload?.counts || payload || {};

        const getCount = (...keys) => {
          for (const key of keys) {
            if (counts?.[key] !== undefined && counts?.[key] !== null) {
              return Number(counts[key]) || 0;
            }
          }
          return 0;
        };

        if (!mounted) return;

        setKpiCounts({
          customers: getCount("customers", "customer_count", "customer"),
          customerVehicles: getCount("customer_vehicles", "customerVehicles", "vehicles"),
          emergencyServices: getCount("emergency_services", "emergencyServices", "services"),
          emergencyServiceVehicles: getCount("emergency_service_vehicles", "emergencyServiceVehicles", "service_vehicles"),
        });
      } catch (err) {
        if (!mounted) return;
        const { message: errMsg } = extractApiError(err);
        console.error("[Dashboard] KPI fetch failed:", errMsg || "Failed to load KPI counts");
        setKpiCounts((prev) => prev);
      } finally {
        if (mounted) setKpiCountsLoading(false);
      }
    };

    fetchKpiCounts();
    return () => {
      mounted = false;
    };
  }, []);

  useIncidentSocket((payload) => {
    const idKey = ["incident_id", "incidentId", "id"].find((k) => payload?.[k] !== undefined);

    const incidentId = idKey ? String(payload[idKey]) : `INC-LIVE-${Date.now()}`;
    if (mapShownIncidents.current.has(incidentId)) return;
    mapShownIncidents.current.add(incidentId);
    incrementLiveAlertBadge();
    // The popup itself is raised once by TopBar (with full incident details).
  });

  useEffect(() => {
    let mounted = true;

    const fetchRecentIncidents = async () => {
      setRecentIncidentsLoading(true);
      setRecentIncidentsError("");

      try {
        const response = await api.get("/incidents", {
          params: { page: 1, page_size: 10 },
        });

        // Extract data array from { data: [...], meta: { ... } } structure
        const incidentsData = response?.data?.data || response?.data || [];
        const rows = Array.isArray(incidentsData) ? incidentsData : [];

        const mapped = Array.isArray(rows) ? rows.map(mapIncident) : [];

        if (!mounted) return;

        setRecentIncidents(mapped.slice(0, 5));
      } catch (err) {
        if (!mounted) return;
        const { message: errMsg } = extractApiError(err);
        setRecentIncidentsError(errMsg || "Failed to load latest incidents");
        setRecentIncidents([]);
        console.error("[Dashboard] Incidents fetch failed:", errMsg || "Failed to load latest incidents");
      } finally {
        if (mounted) setRecentIncidentsLoading(false);
      }
    };

    fetchRecentIncidents();

    return () => {
      mounted = false;
    };
  }, []);

  const handleRecentIncidentClick = useCallback((incident) => {
    if (!incident || !incident.id) return;
    // Navigate to incidents page and ask it to open the incident detail dialog
    navigate("/admin/incidents", { state: { selectedId: String(incident.id) } });
  }, [navigate]);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <PageHeader
        title="Master Control"
        subtitle="National view of every connected vehicle, IoT device and emergency response."
      />

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        <KPITile
          icon={Building2}
          label="Customers"
          value={kpiCountsLoading ? "--" : kpiCounts.customers}
          accent="rose"
          testId="kpi-customers"
        />
        <KPITile
          icon={Car}
          label="Customer Vehicles"
          value={kpiCountsLoading ? "--" : kpiCounts.customerVehicles}
          accent="blue"
          testId="kpi-customer-vehicles"
        />
        <KPITile
          icon={UsersIcon}
          label="Emergency Services"
          value={kpiCountsLoading ? "--" : kpiCounts.emergencyServices}
          accent="indigo"
          testId="kpi-emergency-services"
        />
        <KPITile
          icon={Cpu}
          label="Emergency Service Vehicles"
          value={kpiCountsLoading ? "--" : kpiCounts.emergencyServiceVehicles}
          accent="emerald"
          testId="kpi-emergency-service-vehicles"
        />
      </div>

      {/* Map + Live alerts */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3 fix-hei">
        <div className="xl:col-span-2">
          <SectionCard
            title="Live Vehicle Map"
            subtitle={mapLastUpdated ? `${mapVehicles.length} vehicles · ${incidentMarkers.length} incidents · Updated ${mapLastUpdated.toLocaleTimeString()}` : "Loading vehicle positions…"}
            padded={false}
          >
            <div className="relative p-3">
              {mapLoading && (
                <div className="absolute inset-3 z-10 flex items-center justify-center rounded-2xl bg-white/70 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                    <p className="text-xs font-medium text-slate-500">Loading vehicles…</p>
                  </div>
                </div>
              )}
              <MapView markers={visibleMapMarkers} focusPoint={selectedFocusCoords} height={420} testId="dashboard-map" />
            </div>
          </SectionCard>
        </div>

        <SectionCard title="Critical Alerts" subtitle="Live Events & Vehicles" padded={false}>
          <Tabs value={criticalTab} onValueChange={setCriticalTab} className="px-5 pb-5 pt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="incident">Incidents</TabsTrigger>
              <TabsTrigger value="vehicle">Vehicles</TabsTrigger>
            </TabsList>

            <TabsContent value="incident" className="mt-4">
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100">
                {criticalIncidentItems.map((incident) => (
                  <li
                    key={incident.id}
                    className="cursor-pointer px-4 py-4 transition hover:bg-slate-50"
                    onClick={() => handleFocusCriticalIncident(incident)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">{incident.type}</p>
                      <SeverityBadge severity={incident.severity} />
                    </div>
                    <LocationLabel locationValue={incident.location} className="mt-1 text-sm text-slate-700" />
                    <p className="mt-0.5 text-xs text-slate-500">
                      Status {incident.status} · Event time {incident.eta}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Driver {typeof incident.driver === "string" ? incident.driver : incident.driver?.name || "—"}
                    </p>
                  </li>
                ))}

                {criticalIncidentItems.length === 0 && (
                  <li className="px-4 py-4 text-sm text-slate-500">No live incidents available.</li>
                )}
              </ul>
            </TabsContent>

            <TabsContent value="vehicle" className="mt-4">
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100">
                {criticalVehicleItems.map((vehicle) => (
                  <li
                    key={vehicle.id}
                    className="cursor-pointer px-4 py-4 transition hover:bg-slate-50"
                    onClick={() => handleFocusCriticalVehicle(vehicle)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">{getVehicleDisplayLabel(vehicle)}</p>
                      <StatusBadge status={vehicle.status} />
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{vehicle.model || "Vehicle details"}</p>
                    <LocationLabel locationValue={vehicle.location} className="mt-0.5 text-xs text-slate-500" />
                    <p className="mt-0.5 text-xs text-slate-500">
                      GPS speed {Number.isFinite(vehicle.gps_speed) ? `${vehicle.gps_speed.toFixed(1)} km/h` : "—"}
                      {Number.isFinite(vehicle.obd_speed) ? ` · OBD speed ${vehicle.obd_speed.toFixed(1)} km/h` : ""}
                    </p>
                  </li>
                ))}

                {criticalVehicleItems.length === 0 && (
                  <li className="px-4 py-4 text-sm text-slate-500">No live vehicles available.</li>
                )}
              </ul>
            </TabsContent>
          </Tabs>
          {selectedFocusLabel ? (
            <div className="border-t px-5 py-3 text-xs text-slate-500">
              Focused on: <span className="font-semibold text-slate-700">{selectedFocusLabel}</span>
            </div>
          ) : null}
        </SectionCard>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title="Response Time Trend" subtitle="Average minutes per service">
            <div className="flex items-center justify-end px-3 pb-2">
              <select
                value={responseTimeBucket}
                onChange={(e) => setResponseTimeBucket(e.target.value)}
                className="rounded-md border px-2 py-1 text-sm"
                aria-label="Response time bucket"
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>
            <div className="h-64 relative">
              {responseTimeLoading && (
                <div className="absolute inset-3 z-10 flex items-center justify-center rounded-2xl bg-white/70 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                  </div>
                </div>
              )}
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={responseTimeData}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Area type="monotone" dataKey="police" stroke="#0369A1" strokeWidth={2} fill="url(#g1)" />
                <Area type="monotone" dataKey="ambulance" stroke="#C2410C" strokeWidth={2} fill="transparent" />
                <Area type="monotone" dataKey="fire" stroke="#BE123C" strokeWidth={2} fill="transparent" />
                  <Area type="monotone" dataKey="overall" stroke="#0F172A" strokeWidth={2} fill="transparent" strokeDasharray="4 4" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Accident Trend (Monthly)" subtitle="Detected incidents per month">
          <div className="flex items-center justify-end px-3 pb-2">
            {/* <label className="text-xs text-slate-500 mr-2">Range</label>
            <select
              value={accidentBucket}
              onChange={(e) => setAccidentBucket(e.target.value)}
              className="rounded-md border px-2 py-1 text-sm"
              aria-label="Accident bucket"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select> */}
          </div>
          <div className="h-64 relative">
            {accidentLoading && (
              <div className="absolute inset-3 z-10 flex items-center justify-center rounded-2xl bg-white/70 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                </div>
              </div>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={accidentData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} stroke="#e2e8f0" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="count" fill="#0F172A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Recent incidents */}
      <SectionCard title="Recent Incidents" subtitle="Most recent system-wide events" padded={false}>
        {recentIncidentsError ? (
          <div className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-xs text-amber-700">
            {recentIncidentsError} Showing fallback data.
          </div>
        ) : null}
        <DataTable
          columns={[
            { key: "type", label: "Type", cellClassName: "text-slate-700" },
            { key: "vehicle", label: "Vehicle", cellClassName: "font-mono text-xs text-slate-700" },
            {
              key: "severity",
              label: "Severity",
              render: (incident) => <SeverityBadge severity={incident.severity} />,
            },
            {
              key: "status",
              label: "Status",
              render: (incident) => <StatusBadge status={incident.status} />,
            },
            { key: "eta", label: "ETA", cellClassName: "text-slate-700" },
            // { key: "actions", label: "Actions", className: "text-right" },
          ]}
          data={recentIncidents}
          onRowClick={handleRecentIncidentClick}
          loading={recentIncidentsLoading}
          loadingMessage="Loading recent incidents..."
          emptyMessage="No incidents found."
        >
              {/* <td className="px-5 py-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => toast("View incident", { description: i.displayId || i.id })}>
                      <Eye size={14} /> View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toast("Edit incident", { description: i.displayId || i.id })}>
                      <Pencil size={14} /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-rose-600 focus:text-rose-700" onClick={() => toast("Delete incident", { description: i.displayId || i.id })}>
                      <Trash2 size={14} /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td> */}
        </DataTable>
      </SectionCard>
    </div>
  );
}
